// Learn's pure helpers (docs/areas/learn.md): the outline of a course from its edges, the library's
// grouping and search, and familiarity. Library code: imports a kind's schema or apply, never its index.
import type { Edge } from '@/db/types'
import type { ConceptResource, LessonResource, ModuleResource, SessionResource } from '@/kinds/learn/apply'
import type { Familiarity } from '@/kinds/learn/schema'
import { PATTERN_CATEGORIES } from './learnPatterns'

export const FAMILIARITY_WORD: Record<Familiarity, string> = {
  new: 'New',
  learning: 'Learning',
  known: 'Known',
}

const byOrderThenTitle = (a: ModuleResource | LessonResource, b: ModuleResource | LessonResource) =>
  (a.spec.order ?? 0) - (b.spec.order ?? 0) || a.spec.title.localeCompare(b.spec.title)

const byName = (a: ConceptResource, b: ConceptResource) => a.spec.name.localeCompare(b.spec.name)

const live = <R extends { deleted_at: string | null }>(rows: R[] | undefined): R[] =>
  (rows ?? []).filter((r) => !r.deleted_at)

/** The ids an id points at through one relation. */
const targets = (edges: Edge[], from: string, relation: Edge['relation']) =>
  edges.filter((e) => !e.deleted_at && e.from_id === from && e.relation === relation).map((e) => e.to_id)

export interface LessonNode {
  lesson: LessonResource
  concepts: ConceptResource[]
}
export interface ModuleNode {
  module: ModuleResource
  lessons: LessonNode[]
}

/** A course as a tree: its modules, their lessons and what each lesson teaches, in order. */
export const outlineOf = (
  courseId: string,
  o: {
    modules: ModuleResource[]
    lessons: LessonResource[]
    concepts: ConceptResource[]
    edges: Edge[]
  },
): ModuleNode[] => {
  const modules = new Map(live(o.modules).map((m) => [m.id, m]))
  const lessons = new Map(live(o.lessons).map((l) => [l.id, l]))
  const concepts = new Map(live(o.concepts).map((c) => [c.id, c]))
  return targets(o.edges, courseId, 'has_module')
    .flatMap((id) => modules.get(id) ?? [])
    .sort(byOrderThenTitle)
    .map((module) => ({
      module,
      lessons: targets(o.edges, module.id, 'has_lesson')
        .flatMap((id) => lessons.get(id) ?? [])
        .sort(byOrderThenTitle)
        .map((lesson) => ({
          lesson,
          concepts: targets(o.edges, lesson.id, 'teaches')
            .flatMap((id) => concepts.get(id) ?? [])
            .sort(byName),
        })),
    }))
}

/** Every concept a course teaches, once. */
export const courseConcepts = (tree: ModuleNode[]): ConceptResource[] => {
  const seen = new Map<string, ConceptResource>()
  for (const m of tree) for (const l of m.lessons) for (const c of l.concepts) seen.set(c.id, c)
  return [...seen.values()]
}

/** How many of these concepts are known: the only progress there is (no due counts, no streaks). */
export const knownOf = (concepts: ConceptResource[]) => ({
  known: concepts.filter((c) => c.status.familiarity === 'known').length,
  total: concepts.length,
})

/** The next concept to look at: one being learned, else a new one, in the course's order. */
export const nextUp = (concepts: ConceptResource[]): ConceptResource | undefined =>
  concepts.find((c) => c.status.familiarity === 'learning') ??
  concepts.find((c) => c.status.familiarity === 'new')

/** Whether a concept matches words typed into a search box (name, line, notes), as Search does. */
export const matchConcept = (c: ConceptResource, needle: string): boolean => {
  const n = needle.trim().toLowerCase()
  if (!n) return true
  return [
    c.spec.name,
    c.spec.category,
    c.spec.one_liner,
    c.spec.my_notes,
    c.spec.when_to_use,
    c.spec.when_not_to,
  ]
    .filter((s): s is string => !!s)
    .some((s) => s.toLowerCase().includes(n))
}

export interface CategoryGroup {
  category: string
  concepts: ConceptResource[]
}

const NO_CATEGORY = 'Other'

/** Concepts grouped by category: the pattern categories first in their order, then the rest by name. */
export const groupByCategory = (concepts: ConceptResource[]): CategoryGroup[] => {
  const groups = new Map<string, ConceptResource[]>()
  for (const c of live(concepts)) {
    const key = c.spec.category?.trim() || NO_CATEGORY
    groups.set(key, [...(groups.get(key) ?? []), c])
  }
  const rank = (cat: string) => {
    const i = (PATTERN_CATEGORIES as string[]).indexOf(cat)
    return i < 0 ? PATTERN_CATEGORIES.length : i
  }
  return [...groups.entries()]
    .sort(
      ([a], [b]) =>
        rank(a) - rank(b) || (a === NO_CATEGORY ? 1 : b === NO_CATEGORY ? -1 : a.localeCompare(b)),
    )
    .map(([category, rows]) => ({ category, concepts: rows.sort(byName) }))
}

/** The concepts a concept is related to, either way round, once each. */
export const relatedOf = (id: string, concepts: ConceptResource[], edges: Edge[]): ConceptResource[] => {
  const ids = new Set<string>()
  for (const e of edges) {
    if (e.deleted_at || e.relation !== 'related_to') continue
    if (e.from_id === id) ids.add(e.to_id)
    else if (e.to_id === id) ids.add(e.from_id)
  }
  return live(concepts)
    .filter((c) => ids.has(c.id))
    .sort(byName)
}

/** Lessons done of those that count (skipped ones are set aside): the bar on Learn's home. */
export const lessonProgress = (tree: ModuleNode[]): { done: number; total: number } => {
  const all = tree.flatMap((m) => m.lessons).filter((l) => l.lesson.status.state !== 'skipped')
  return { done: all.filter((l) => l.lesson.status.state === 'done').length, total: all.length }
}

export interface ModuleProgress {
  module: ModuleResource
  new: number
  learning: number
  known: number
  total: number
}

/** Each module with how many of its concepts are new, learning and known: familiarity only. */
export const moduleProgress = (tree: ModuleNode[]): ModuleProgress[] =>
  tree.map(({ module, lessons }) => {
    const cs = courseConcepts([{ module, lessons }])
    const n = (f: Familiarity) => cs.filter((c) => c.status.familiarity === f).length
    return { module, new: n('new'), learning: n('learning'), known: n('known'), total: cs.length }
  })

export interface DeckDone {
  id: string
  at: string
  right: number
  partial: number
  wrong: number
}

/** The decks finished, newest first, and how their answers went (`tests`: the tests instead). Only ones that were finished. */
export const deckHistory = (sessions: SessionResource[], limit = 12, tests = false): DeckDone[] =>
  live(sessions)
    .filter((s) => s.status.ended_at && (s.status.items_done ?? 0) > 0 && (s.spec.mode === 'test') === tests)
    .sort((a, b) => b.status.ended_at!.localeCompare(a.status.ended_at!))
    .slice(0, limit)
    .map((s) => ({
      id: s.id,
      at: s.status.ended_at!,
      right: s.status.right ?? 0,
      partial: s.status.partial ?? 0,
      wrong: s.status.wrong ?? 0,
    }))
